/* eslint-disable check-file/filename-blocklist */
/** @jsx jsx */
import {
	forwardRef,
	type ButtonHTMLAttributes,
	type ClassAttributes,
	type ComponentType,
	type ForwardRefExoticComponent,
	type HTMLAttributes,
	type RefAttributes,
} from 'react';
import type { StyledProps } from '@compiled/react';
import { styled, css, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

type ReactionsContainerProps = {
	isDisabled?: boolean;
	hasReactions?: boolean;
	readOnly?: boolean;
};

// helper function for page comment reactions styles
const getPageCommentLeftMargin = (isDisabled) => {
	if (isDisabled) {
		// reactions-spacer is 5px wide
		return '0';
	} else {
		return '5px';
	}
};

const reactionsContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	// TODO: remove this once fixed in @atlaskit/reactions
	// reactions component shouldn't have any spacing around it, this css fix will be available soon in @atlaskit/reactions
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"& div[data-testid='render-reactions']": {
		marginTop: token('space.negative.050'),
	},
	// size & color of emoji in reaction
	// TODO: move picker icon color style in @atlaskit/reactions
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"& span[aria-label='Add reaction'] > svg[role='presentation']": {
		color: token('color.text.subtle'),
	},

	// TODO: remove this after nikita release, as margins are fixed in @atlaskit/reactions
	// spacing between reactions - only apply to reaction wrapper & reaction picker, not the buttons within reactions menu
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"& button[data-testid='render_reaction_wrapper']": {
		margin: `${token('space.050')} ${token('space.050')} 0px 0px`,
	},

	// TODO: remove this after nikita release, as margins are fixed in @atlaskit/reactions
	// spacing for reactions picker button
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"& div[data-testid='reactionPicker-testid']": {
		margin: `${token('space.050')} 0px 0px 0px`,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& button': {
		overflow: 'hidden',

		// max width of emoji images
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'img[data-emoji-id]': {
			maxWidth: '30px',
		},
	},
});

// styling for disable state of reactions
const disabledOrReadOnlyStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& *': {
		cursor: 'not-allowed',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"& span[aria-label='Add reaction'] > svg[role='presentation']": {
		color: token('color.text.subtlest'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& button': {
		cursor: 'not-allowed',
		pointerEvents: 'none',
	},
});

// hide the add reaction button and users dialog link for read-only
const readOnlyStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"& div[data-testid='reactionPicker-testid'], & button[data-testid='viewall-reacted-users-dialog']":
		{
			display: 'none',
		},
});

export const ReactionsContainer = ({
	isDisabled,
	hasReactions: _hasReactions,
	readOnly,
	...props
}: {
	[x: string]: any;
	isDisabled: any;
	hasReactions: any;
	readOnly: any;
}): JSX.Element => {
	'use no memo';
	return (
		<div
			css={[
				reactionsContainerStyles,
				(isDisabled || readOnly) && disabledOrReadOnlyStyles,
				readOnly && readOnlyStyles,
			]}
			{...props}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PageReactionsContainer: ComponentType<
	{
		[x: string]: any;
		isDisabled: any;
		hasReactions: any;
		readOnly: any;
	} & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled(ReactionsContainer)({
	margin: '4px 20px 0 0',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PageCommentReactionsContainer: ComponentType<
	ReactionsContainerProps & {
		[x: string]: any;
		isDisabled: any;
		hasReactions: any;
		readOnly: any;
	} & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled(
	ReactionsContainer,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
)<ReactionsContainerProps>(({ isDisabled }) => ({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginLeft: getPageCommentLeftMargin(isDisabled),
	marginTop: 4,
}));

// apply for inline comment on view or edit page
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const InlineCommentReactionsContainer: ComponentType<
	ReactionsContainerProps & {
		[x: string]: any;
		isDisabled: any;
		hasReactions: any;
		readOnly: any;
	} & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled(
	ReactionsContainer,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
)<ReactionsContainerProps>(({ isDisabled }) => ({
	// TODO: review and move colors in @atlaskit/reactions
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"span[aria-label='Add reaction'] > svg[role='presentation']": {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		color: isDisabled ? token('color.text.subtlest') : token('color.text.subtle'),
	},
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FeedReactionsContainer: ComponentType<
	ReactionsContainerProps & {
		[x: string]: any;
		isDisabled: any;
		hasReactions: any;
		readOnly: any;
	} & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled(ReactionsContainer)<ReactionsContainerProps>({
	marginTop: 4,
});

// Original empty-state footer styling, retained as the gate-off fallback so that disabling
// `add_a_reaction_bug` restores the previous behaviour rather than the platform default.
const objectCommentFooterReactionsLegacyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',

	// The :not()s prevent these overrides from also applying to the "add new" button when
	// hoverableSummaryView is enabled.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	"span[aria-label='Add reaction'] > svg[role='presentation']:not(div[data-testid='reactionPickerPanel-testid'] span[aria-label='Add reaction'] > svg[role='presentation'])":
		{
			color: token('color.text.subtle'),
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '14px',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	"button:not(div[data-testid='reactionPickerPanel-testid'] button)": {
		padding: `0 ${token('space.150')}`,
		height: token('space.400'),
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	"button div:not(div[data-testid='reactionPickerPanel-testid'] button div)": {
		color: token('color.text.subtle'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '14px',
		marginLeft: token('space.100'),
	},
});

const objectCommentFooterReactionsStyles = css({
	// Left-align the empty-state trigger.
	flexDirection: 'column',
	alignItems: 'flex-start',

	// Square corners, 32px height/padding, and overflow:visible so the emoji-add icon isn't clipped —
	// matches the adjacent "Add labels" button.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	"& button[data-testid='render-trigger-button']": {
		padding: `0 ${token('space.150')}`,
		height: token('space.400'),
		overflow: 'visible',
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
	},

	// Match the "Add labels" font/colour. The `font` shorthand is required to override the platform
	// trigger's own `font` shorthand.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	"& button[data-testid='render-trigger-button'] div": {
		color: token('color.text.subtle'),
		font: token('font.body'),
		marginLeft: token('space.100'),
	},
});

// This container defaults to shrink-to-fit like its flex children below — without this, expanding
// would right-align pills inside a content-width box instead of the row's real available width.
const footerReactionsExpandedContainerStyles = css({
	width: '100%',
});

// The render params are intentionally untyped: the ambient `css` prop in this package resolves to
// an emotion/compiled intersection that rejects compiled `css()` objects unless the surrounding
// values are `any` (mirrors the other containers in this file). The public, consumer-facing props
// are still type-checked via the `forwardRef` generics below.
export const ObjectCommentFooterReactionsContainer: ForwardRefExoticComponent<
	ReactionsContainerProps &
		HTMLAttributes<HTMLDivElement> &
		RefAttributes<HTMLDivElement> & { isFooterReactionsExpanded?: boolean }
> = forwardRef<
	HTMLDivElement,
	ReactionsContainerProps & HTMLAttributes<HTMLDivElement> & { isFooterReactionsExpanded?: boolean }
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
>(({ isDisabled, hasReactions, readOnly, isFooterReactionsExpanded, ...props }: any, ref) => (
	<div
		ref={ref}
		css={[
			reactionsContainerStyles,
			(isDisabled || readOnly) && disabledOrReadOnlyStyles,
			readOnly && readOnlyStyles,
			// Gate on: apply the restyled trigger. Gate off: fall back to the original empty-state styling.
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			fg('add_a_reaction_bug')
				? objectCommentFooterReactionsStyles
				: !hasReactions && objectCommentFooterReactionsLegacyStyles,
			isFooterReactionsExpanded && footerReactionsExpandedContainerStyles,
		]}
		{...props}
	/>
));

ObjectCommentFooterReactionsContainer.displayName = 'ObjectCommentFooterReactionsContainer';

// The collapsed/expanded reaction row. Collapsed stays `nowrap` (bounded pill count + toggle +
// trigger) so the adjacent comment trigger shrinks instead; expanded wraps, right-aligned.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FooterReactionsRow: ComponentType<
	ClassAttributes<HTMLDivElement> &
		HTMLAttributes<HTMLDivElement> &
		StyledProps & { 'data-expanded'?: 'true' | 'false' }
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled.div({
	display: 'flex',
	flexWrap: 'nowrap',
	alignItems: 'center',
	gap: token('space.050'),
	// Unwraps `@atlaskit/reactions`' nested wrapper elements (including the `<ul>` an a11y gate
	// can add) so individual pills become direct flex items of this row instead of one opaque
	// box — required for per-pill wrapping and `flex-end` alignment to work.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	"& div[data-testid='render-reactions'], & div[data-testid='render-reactions'] [role='group'], & div[data-testid='render-reactions'] ul":
		{
			display: 'contents',
		},
	// `&&` doubles this selector's specificity so it reliably beats `reactionsContainerStyles`'s
	// conflicting margin on the same selector (equal specificity otherwise, order-dependent).
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	"&& button[data-testid='render_reaction_wrapper'], && button[data-testid='render-trigger-button']":
		{
			height: token('space.400'),
			alignItems: 'center',
			margin: token('space.0'),
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
			borderRadius: token('radius.medium', '6px'),
		},
	// `reactionPicker-testid` (not the deeper `render-trigger-container`) is the trigger's actual
	// direct flex-item sibling to the pills in this row — `order` here is what lets the "+N"
	// toggle (a plain sibling of the single `AkReactions` instance) render between pills and
	// trigger regardless of DOM position. `marginTop: 0` cancels this element's own 4px top
	// margin (meant for `@atlaskit/reactions`' default wrapping layout), which otherwise skews
	// this item off-center relative to the equal-height pills beside it.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	"& div[data-testid='reactionPicker-testid']": {
		display: 'flex',
		alignItems: 'center',
		height: token('space.400'),
		marginTop: 0,
		order: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&[data-expanded="true"]': {
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
		width: '100%',
	},
});

// Styled to match the reaction pills it sits next to, rather than as plain subtle text.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FooterReactionsExpandToggle: ComponentType<
	ClassAttributes<HTMLButtonElement> & ButtonHTMLAttributes<HTMLButtonElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled.button({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: token('space.400'),
	padding: `0 ${token('space.100')}`,
	// Matches the reaction count text exactly (`Counter.tsx`'s default, non-highlighted style).
	color: token('color.text.subtlest'),
	font: token('font.body.small'),
	backgroundColor: token('color.background.neutral.subtle'),
	borderWidth: token('border.width'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.medium', '6px'),
	cursor: 'pointer',
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	'&:active': {
		backgroundColor: token('color.background.neutral.subtle.pressed'),
	},
	'&:focus-visible': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const MutationErrorContainer: ComponentType<
	ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled.div({
	minWidth: 240,
	marginTop: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const NoReactionsContainer: ComponentType<
	ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled.div({
	marginLeft: token('space.050'),
});
